/**
 * Activity Feed Component
 * Shows recent device activations and updates
 */

import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, styled } from '@mui/material';
import { Card } from '../ui/Card';
import { colors } from '../../theme/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UpdateIcon from '@mui/icons-material/Update';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

interface Activity {
  id: string;
  type: 'activation' | 'update' | 'new';
  device_id: string;
  timestamp: string;
  description: string;
}

interface ActivityFeedProps {
  activities?: Activity[];
}

const ActivityItem = styled(ListItem)({
  padding: '12px 0',
  borderBottom: `1px solid ${colors.neutral[100]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(8, 131, 149, 0.02)',
  },
});

const TimeText = styled(Typography)({
  fontSize: '0.75rem',
  color: colors.text.secondary,
  fontFamily: '"JetBrains Mono", monospace',
});

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities = [] }) => {
  const getIcon = (type: Activity['type']) => {
    switch (type) {
      case 'activation':
        return <CheckCircleIcon sx={{ color: colors.success.main }} />;
      case 'update':
        return <UpdateIcon sx={{ color: colors.secondary.main }} />;
      case 'new':
        return <AddCircleOutlineIcon sx={{ color: colors.primary.main }} />;
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffInMinutes < 1) return 'только что';
    if (diffInMinutes < 60) return `${diffInMinutes} мин назад`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} ч назад`;
    return `${Math.floor(diffInMinutes / 1440)} дн назад`;
  };

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        Последняя активность
      </Typography>

      <Card variant="default">
        <Box sx={{ p: 3 }}>
          {activities.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
              }}
            >
              <Typography
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.875rem',
                }}
              >
                Нет недавней активности
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {activities.slice(0, 5).map((activity) => (
                <ActivityItem key={activity.id}>
                  <ListItemIcon sx={{ minWidth: 40 }}>{getIcon(activity.type)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.primary,
                          mb: 0.5,
                        }}
                      >
                        {activity.description}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeText>{getRelativeTime(activity.timestamp)}</TimeText>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: colors.text.secondary,
                            fontFamily: '"JetBrains Mono", monospace',
                          }}
                        >
                          •
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: colors.text.secondary,
                            fontFamily: '"JetBrains Mono", monospace',
                          }}
                        >
                          {activity.device_id}
                        </Typography>
                      </Box>
                    }
                  />
                </ActivityItem>
              ))}
            </List>
          )}
        </Box>
      </Card>
    </Box>
  );
};
